import * as THREE from 'three';
import { periodicNoise, seedOf } from './noise.js';

/**
 * The sun, and the sky it is in, off the game clock.
 *
 * Scottsdale on an autumn day: up at a quarter past six, highest in the south
 * at about fifty-seven degrees, down at twenty-five past six in the evening,
 * then dusk, the blue hour, and night by a quarter to eight. The evening shift
 * runs two till ten, so it starts in full sun and ends under the lot lights.
 *
 * All of it is a function of a number of minutes, handed in. Nothing here
 * reads a clock and nothing here could change one.
 */

export const RISE = 6 * 60 + 15;
export const SET = 18 * 60 + 25;
const PEAK = 57;
/** Degrees the sun falls below the horizon per minute after it sets, near the equinox here. */
const SINK = 0.2;

/**
 * Where the sun is: elevation in degrees (negative below the horizon) and a
 * unit direction toward it in world space. North is -z and east is +x, which
 * is how the level's walls are named.
 */
export function sunAt(minutes) {
  const m = ((minutes % 1440) + 1440) % 1440;
  let elevation;
  let across;
  if (m >= RISE && m <= SET) {
    across = (m - RISE) / (SET - RISE);
    elevation = PEAK * Math.sin(Math.PI * across);
  } else {
    const after = m > SET ? m - SET : m + 1440 - SET;
    const before = m < RISE ? RISE - m : RISE + 1440 - m;
    elevation = -Math.min(after, before) * SINK;
    across = after < before ? 1 : 0;
  }
  const az = Math.PI * Math.min(1, Math.max(0, across));
  const el = (Math.max(elevation, 2) * Math.PI) / 180;
  const direction = { x: Math.cos(az) * Math.cos(el), y: Math.sin(el), z: Math.sin(az) * Math.cos(el) };
  return { elevation, direction };
}

/**
 * What the sky and the sunlight look like at an elevation: keyframes, blended.
 * Colours are hex; `sun` is the directional light's intensity.
 */
const KEYS = [
  { at: -18, zenith: 0x04060c, horizon: 0x0b0f1a, sunColour: 0x8ea8d8, sun: 0.35, sky: 0.22, stars: 1 },
  { at: -10, zenith: 0x0c1430, horizon: 0x2a2c4c, sunColour: 0x8ea8d8, sun: 0.35, sky: 0.28, stars: 0.6 },
  { at: -4, zenith: 0x24325e, horizon: 0x9a5a6a, sunColour: 0xc08090, sun: 0.12, sky: 0.35, stars: 0.1 },
  { at: 0, zenith: 0x3c5488, horizon: 0xe8845a, sunColour: 0xff9a60, sun: 0.9, sky: 0.55, stars: 0 },
  { at: 6, zenith: 0x4f76b4, horizon: 0xf2b27a, sunColour: 0xffc48a, sun: 2.2, sky: 0.8, stars: 0 },
  { at: 20, zenith: 0x3f7cc6, horizon: 0xcfdbe6, sunColour: 0xfff0dc, sun: 3.0, sky: 1.0, stars: 0 },
  { at: 60, zenith: 0x2f6dbe, horizon: 0xbcd4ea, sunColour: 0xfff8f0, sun: 3.3, sky: 1.05, stars: 0 },
];

export function skyAt(elevation) {
  let i = 0;
  while (i < KEYS.length - 2 && elevation > KEYS[i + 1].at) i += 1;
  const a = KEYS[i];
  const b = KEYS[i + 1];
  const t = Math.min(1, Math.max(0, (elevation - a.at) / (b.at - a.at)));
  const colour = (p, q) => new THREE.Color(p).lerp(new THREE.Color(q), t);
  return {
    zenith: colour(a.zenith, b.zenith),
    horizon: colour(a.horizon, b.horizon),
    sunColour: colour(a.sunColour, b.sunColour),
    sun: a.sun + (b.sun - a.sun) * t,
    sky: a.sky + (b.sky - a.sky) * t,
    stars: a.stars + (b.stars - a.stars) * t,
    /** 1 once the sun is down far enough that the outside lights come on. */
    dark: Math.min(1, Math.max(0, (2 - elevation) / 6)),
  };
}

const VERTEX = `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  vec4 p = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * p;
  gl_Position.z = gl_Position.w;
}`;

const FRAGMENT = `
uniform vec3 zenith;
uniform vec3 horizon;
uniform vec3 ground;
uniform vec3 sunDir;
uniform vec3 sunColour;
uniform float stars;
varying vec3 vDir;
float hash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 c = mix(horizon, zenith, pow(clamp(h, 0.0, 1.0), 0.55));
  c = mix(c, ground, smoothstep(0.0, -0.08, h));
  float s = max(dot(d, normalize(sunDir)), 0.0);
  c += sunColour * (pow(s, 900.0) * 6.0 + pow(s, 12.0) * 0.25) * step(-0.02, sunDir.y);
  if (stars > 0.0 && h > 0.05) {
    vec3 q = floor(d * 420.0);
    float st = step(0.9975, hash(q));
    c += vec3(st) * stars * 0.9;
  }
  gl_FragColor = vec4(c, 1.0);
  #include <colorspace_fragment>
}`;

/**
 * The sky dome, the desert floor past the walls, and the mountains on the
 * horizon — the McDowells to the east, low hills round the rest.
 */
export function createSky({ ground: groundTexture }) {
  const group = new THREE.Group();
  group.name = 'look-sky';

  const uniforms = {
    zenith: { value: new THREE.Color() },
    horizon: { value: new THREE.Color() },
    ground: { value: new THREE.Color(0x6a5440) },
    sunDir: { value: new THREE.Vector3(0, 1, 0) },
    sunColour: { value: new THREE.Color() },
    stars: { value: 0 },
  };
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(380, 32, 16),
    new THREE.ShaderMaterial({ uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT, side: THREE.BackSide, depthWrite: false, fog: false })
  );
  dome.name = 'sky-dome';
  dome.renderOrder = -10;
  dome.frustumCulled = false;
  group.add(dome);

  // The desert floor, under and past the property. Below every floor box, so it
  // only shows where there is no building.
  const floorMaterial = new THREE.MeshStandardMaterial({ map: groundTexture.color, roughness: 1, color: 0xe8d8c0 });
  const desert = new THREE.Mesh(new THREE.PlaneGeometry(900, 900), floorMaterial);
  desert.rotation.x = -Math.PI / 2;
  desert.position.set(0, -0.04, -6);
  desert.receiveShadow = true;
  desert.name = 'desert-floor';
  // World-metre texture coordinates, so the grit is the same size as the lot's.
  const uv = desert.geometry.attributes.uv;
  for (let i = 0; i < uv.count; i += 1) uv.setXY(i, (uv.getX(i) - 0.5) * 900, (uv.getY(i) - 0.5) * 900);
  group.add(desert);

  // The ridgeline: a ring of peaks, higher in the east.
  const ridge = periodicNoise(64, seedOf('mcdowells'));
  const segments = 256;
  const positions = [];
  const radius = 330;
  for (let i = 0; i <= segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    const east = Math.max(0, Math.cos(a));
    const height = 8 + ridge((i / segments) * 64, 3) * 22 + ridge((i / segments) * 64 * 4, 9) * 6 + east * east * 38;
    const x = Math.cos(a) * radius;
    const z = Math.sin(a) * radius;
    positions.push(x, -2, z, x, height, z);
  }
  const indices = [];
  for (let i = 0; i < segments; i += 1) {
    const b = i * 2;
    indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
  }
  const ridgeGeometry = new THREE.BufferGeometry();
  ridgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  ridgeGeometry.setIndex(indices);
  const ridgeMaterial = new THREE.MeshBasicMaterial({ color: 0x5a4a5a, fog: false, side: THREE.DoubleSide });
  const mountains = new THREE.Mesh(ridgeGeometry, ridgeMaterial);
  mountains.name = 'mountains';
  mountains.frustumCulled = false;
  mountains.renderOrder = -9;
  group.add(mountains);

  return {
    object3D: group,
    /** One frame: follow the camera, and colour everything for the hour. */
    update({ camera, sun, sky }) {
      dome.position.copy(camera);
      mountains.position.set(camera.x, 0, camera.z);
      uniforms.zenith.value.copy(sky.zenith);
      uniforms.horizon.value.copy(sky.horizon);
      uniforms.sunDir.value.set(sun.direction.x, Math.sin((sun.elevation * Math.PI) / 180), sun.direction.z);
      uniforms.sunColour.value.copy(sky.sunColour);
      uniforms.stars.value = sky.stars;
      // Hazy blue-violet by day, a silhouette against the dusk, black at night.
      ridgeMaterial.color.copy(sky.horizon).lerp(new THREE.Color(0x3c3448), 0.55).multiplyScalar(0.35 + 0.55 * sky.sky);
    },
    dispose() {
      dome.geometry.dispose();
      dome.material.dispose();
      desert.geometry.dispose();
      floorMaterial.dispose();
      ridgeGeometry.dispose();
      ridgeMaterial.dispose();
    },
  };
}
